import { useCallback, useEffect, useState } from 'react';

/**
 * The webmail directions, and light/dark.
 *
 * They are a user preference rather than a build-time constant because the
 * mockup presented them as a choice to be made by looking at real mail, and
 * that choice is not made yet. Switching is a click, so it stays cheap to
 * change our minds.
 *
 * `outlook` is the one look that is not from the mockup: the layout people
 * moving from Outlook on the web already know, in our own markup (Edmund,
 * 2026-10-29).
 */
export type Look = 'signal' | 'quiet' | 'console' | 'outlook';
export type Mode = 'light' | 'dark';

export const LOOKS: readonly Look[] = ['signal', 'quiet', 'console', 'outlook'];

export interface Theme {
  look: Look;
  mode: Mode;
}

const STORAGE_KEY = 'zitimail.theme';

/** Follows the operating system until the user picks something. */
function preferredMode(): Mode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function readStoredTheme(): Theme {
  const fallback: Theme = { look: 'signal', mode: preferredMode() };
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw) as Partial<Theme>;
    return {
      look: LOOKS.includes(parsed.look as Look) ? (parsed.look as Look) : fallback.look,
      mode: parsed.mode === 'dark' || parsed.mode === 'light' ? parsed.mode : fallback.mode,
    };
  } catch {
    // A corrupt or unreadable preference is not worth failing a page load over.
    return fallback;
  }
}

/**
 * Applies the theme to the document root, where the token blocks select on it,
 * and remembers it. Every visual difference between the looks is CSS selected
 * on `[data-look][data-mode]` (mostly variables; `outlook` also reshapes a few
 * components), so this is the only place in the code that has to know the
 * theme exists.
 */
export function useTheme(): {
  theme: Theme;
  setLook: (look: Look) => void;
  setMode: (mode: Mode) => void;
} {
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.look = theme.look;
    root.dataset.mode = theme.mode;
    // Lets the browser paint scrollbars and form controls to match.
    root.style.colorScheme = theme.mode;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
    } catch {
      // Private browsing can refuse writes; the theme still applies.
    }
  }, [theme]);

  const setLook = useCallback((look: Look) => setTheme((t) => ({ ...t, look })), []);
  const setMode = useCallback((mode: Mode) => setTheme((t) => ({ ...t, mode })), []);

  return { theme, setLook, setMode };
}
